import { formatUnits, parseUnits } from 'viem'

import { TOKEN_PRICE_DECIMALS } from '~/constants/tokens'

export const ALLOWED_DECIMAL_SEPARATORS = ['.', ',']

export const getNumberFromBigNumber = (
  bigNumber: bigint,
  decimals: number = 18,
): number => {
  return parseFloat(formatUnits(bigNumber, decimals))
}

export const getBigNumberFromNumber = (
  number: number,
  decimals: number = 18,
): bigint => {
  let value = number.toString()
  if (value.includes('e') || value.includes('E')) {
    // parseUnits sometimes explodes when it receives some large strings with
    // scientific notation so this function expands the exponent
    value = scientificToDecimal(number)
  }
  return parseUnits(value, decimals)
}

export const formatWholeNumber = (number: number) => {
  return number.toLocaleString('en-US')
}

const scientificToDecimal = (number: number) => {
  return number.toLocaleString('en-US', { useGrouping: false })
}

export const scientificNotationToDecimalString = (str: string): string => {
  // no need to convert if it's not in scientific notation
  if (!str.includes('e-') && !str.includes('E-')) {
    return str
  }

  // the mantissa is the "signifant" part of the number before the e-
  const [mantissa, exponentStr] = str.toLowerCase().split('e-')
  const exponent = Math.abs(parseInt(exponentStr))

  const [integerPart, decimalPart = ''] = mantissa.split('.')
  const fullNumber = integerPart + decimalPart

  const isNegative = fullNumber.startsWith('-')
  const absoluteNumber = isNegative ? fullNumber.slice(1) : fullNumber

  let result = '0.'
  // we've already concatted the integer part and decimal part,
  // so we need one less zero than the exponent
  const zerosNeeded = exponent - 1
  result += '0'.repeat(zerosNeeded)
  result += absoluteNumber

  return isNegative ? '-' + result : result
}

export const roundToDecimals: (arg0: number, arg1: number) => number = (
  num,
  decimals,
) => {
  const multiplier = Math.pow(10, decimals)
  return Math.round((num + Number.EPSILON) * multiplier) / multiplier
}

export const ceilToDecimals = (num: number, decimals: number): number => {
  const multiplier = Math.pow(10, decimals)
  return Math.ceil((num - Number.EPSILON) * multiplier) / multiplier
}

export const multiplyWithPrecision = (
  first: { amount: string; decimal: number },
  second: { amount: string; decimal: number },
): string => {
  const { amount: amount0, decimal: decimal0 } = first
  const { amount: amount1, decimal: decimal1 } = second

  const finalAmount0 = scientificNotationToDecimalString(amount0)
  const finalAmount1 = scientificNotationToDecimalString(amount1)

  return formatUnits(
    (parseUnits(finalAmount0, decimal0) * parseUnits(finalAmount1, decimal1)) /
      BigInt(10 ** decimal1),
    decimal0,
  )
}

/**
 * This function is used to convert a USD value to a token amount.
 * @param tokenUSDValue - The USD value of the token (assumed to be in TOKEN_PRICE_DECIMALS)
 * @param tokenDecimals - The decimals of the token
 * @param tokenPrice - The price of the token in USD (assumed to be in TOKEN_PRICE_DECIMALS)
 * @returns The amount of tokens in the token's decimals
 */
export const getAmountFromUSDValue = (options: {
  tokenUSDValue: string
  tokenDecimals: number
  tokenPrice: string
}): string => {
  const { tokenUSDValue, tokenDecimals, tokenPrice } = options
  const safeTokenPrice = scientificNotationToDecimalString(tokenPrice)
  const parsedTokenPrice = parseUnits(safeTokenPrice, TOKEN_PRICE_DECIMALS)

  if (parsedTokenPrice === 0n) {
    return '0'
  }

  return formatUnits(
    (parseUnits(tokenUSDValue, TOKEN_PRICE_DECIMALS) *
      BigInt(10 ** tokenDecimals)) /
      parsedTokenPrice,
    tokenDecimals,
  )
}

// Used to parse input values. They can be:
// 1. undefined
// 2. ''
// 3. A valid number
// 4. An invalid number
//
// Only invalid numbers should be NaN. We should not allow users to input these values,
// but both `undefined` and `` are valid states in which an input value can be in.
export const parseInputValueAsFloat = (value: string | undefined) => {
  if (!value || value.trim() === '') {
    return 0
  }
  return parseFloat(value)
}
